import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CreditCard, Sparkles, Clock } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import axiosInstance from '@/utils/axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { formatPrice } from '@/utils/formatters';

const PaymentPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [properties, setProperties] = useState([]);
  const [buyerInfo, setBuyerInfo] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || ''
  });
  const [processing, setProcessing] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [couponValidation, setCouponValidation] = useState(null);
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  
  // Read URL params for pre-selection
  const urlPropertyId = searchParams.get('property');
  const urlPlanType = searchParams.get('plan');

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchPlans();
    fetchUserProperties();
  }, [isAuthenticated]);

  const fetchPlans = async () => {
    try {
      const { data } = await axiosInstance.get('/payments/plans');
      setPlans(data.plans);
      
      // Auto-select plan based on URL param or default to "publish"
      const targetPlanType = urlPlanType || 'publish';
      const targetPlan = data.plans.find(p => p.type === targetPlanType);
      if (targetPlan) {
        setSelectedPlan(targetPlan);
      }
    } catch (error) {
      console.error('Error fetching plans:', error);
      toast.error('Error al cargar los planes de pago');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserProperties = async () => {
    try {
      const { data } = await axiosInstance.get('/properties/user/my-properties');
      setProperties(data.properties);
      
      // Auto-select property based on URL param first
      if (urlPropertyId) {
        const targetProperty = data.properties.find(p => p.id === urlPropertyId);
        if (targetProperty) {
          setSelectedProperty(urlPropertyId);
          return;
        }
      }
      
      // Otherwise auto-select the most recent DRAFT property
      const draftProperty = data.properties.find(p => p.status === 'DRAFT');
      if (draftProperty) {
        setSelectedProperty(draftProperty.id);
      }
    } catch (error) {
      console.error('Error fetching properties:', error);
    }
  };

  const handlePlanSelect = (plan) => {
    setSelectedPlan(plan);
    setCouponValidation(null); // Reset coupon validation when plan changes
  };

  const handleValidateCoupon = async () => {
    if (!couponCode.trim()) {
      toast.error('Ingresa un código de cupón');
      return;
    }

    if (!selectedPlan) {
      toast.error('Primero selecciona un plan');
      return;
    }

    setValidatingCoupon(true);

    try {
      const { data } = await axiosInstance.post('/coupons/validate', {
        code: couponCode,
        plan_type: selectedPlan.type,
        amount: selectedPlan.price
      });

      if (data.valid) {
        setCouponValidation(data);
        toast.success(data.message);
      } else {
        setCouponValidation(null);
        toast.error(data.message);
      }
    } catch (error) {
      console.error('Error validating coupon:', error);
      setCouponValidation(null);
      toast.error('Error al validar el cupón');
    } finally {
      setValidatingCoupon(false);
    }
  };

  const handleRemoveCoupon = () => {
    setCouponCode('');
    setCouponValidation(null);
  };

  const handlePayment = async () => {
    if (!selectedProperty) {
      toast.error('Selecciona una propiedad');
      return;
    }

    if (!selectedPlan) {
      toast.error('Selecciona un plan');
      return;
    }

    if (!buyerInfo.name || !buyerInfo.email || !buyerInfo.phone) {
      toast.error('Completa toda tu información');
      return;
    }

    setProcessing(true);

    try {
      const { data } = await axiosInstance.post('/payments/create', {
        property_id: selectedProperty,
        payment_type: selectedPlan.type,
        buyer_name: buyerInfo.name,
        buyer_email: buyerInfo.email,
        buyer_phone: buyerInfo.phone,
        coupon_code: couponValidation?.valid ? couponCode : null
      });

      // Redirect to MercadoPago
      if (data.init_point) {
        window.location.href = data.init_point;
      } else if (data.sandbox_init_point) {
        window.location.href = data.sandbox_init_point;
      }
    } catch (error) {
      console.error('Error creating payment:', error);
      
      let errorMessage = 'Error al procesar el pago';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        }
      }
      
      toast.error(errorMessage);
    } finally {
      setProcessing(false);
    }
  };

  const getPlanIcon = (type) => {
    if (type === 'publish') return <CreditCard className="w-6 h-6" />;
    return <Sparkles className="w-6 h-6" />;
  };

  const getPlanBadge = (type) => {
    if (type === 'publish') return <Badge className="bg-primary">Publicar</Badge>;
    if (type === 'feature_7') return <Badge className="bg-accent">7 días</Badge>;
    return <Badge className="bg-secondary">30 días</Badge>;
  };

  if (!isAuthenticated) return null;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-12">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="text-center mb-12">
            <h1 className="text-4xl font-heading font-bold text-slate-900 mb-3">
              Planes de Pago
            </h1>
            <p className="text-lg text-slate-600">
              Publica o destaca tu propiedad para llegar a más compradores
            </p>
          </div>

          {/* Plans Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12" data-testid="plans-grid">
            {plans.map((plan) => (
              <motion.div
                key={plan.type}
                whileHover={{ y: -4 }}
                className={`cursor-pointer ${
                  selectedPlan?.type === plan.type ? 'ring-2 ring-primary' : ''
                }`}
                onClick={() => handlePlanSelect(plan)}
                data-testid={`plan-${plan.type}`}
              >
                <Card className="h-full hover:shadow-xl transition-shadow duration-300">
                  <CardHeader>
                    <div className="flex items-center justify-between mb-4">
                      {getPlanIcon(plan.type)}
                      {getPlanBadge(plan.type)}
                    </div>
                    <CardTitle className="text-2xl font-heading">{plan.name}</CardTitle>
                    <CardDescription>{plan.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="mb-4">
                      <p className="text-4xl font-heading font-bold text-primary">
                        {formatPrice(plan.price)}
                      </p>
                      <div className="flex items-center text-slate-600 mt-2">
                        <Clock className="w-4 h-4 mr-1" />
                        <span className="text-sm">{plan.duration_days} días</span>
                      </div>
                    </div>
                  </CardContent>
                  <CardFooter>
                    <Button
                      className={`w-full ${
                        selectedPlan?.type === plan.type
                          ? 'bg-primary hover:bg-primary-hover'
                          : 'bg-slate-200 hover:bg-slate-300 text-slate-700'
                      }`}
                      onClick={() => handlePlanSelect(plan)}
                    >
                      {selectedPlan?.type === plan.type ? 'Seleccionado' : 'Seleccionar'}
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* Payment Form */}
          {selectedPlan && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-white rounded-2xl p-8 border border-slate-200 max-w-2xl mx-auto"
              data-testid="payment-form"
            >
              <h2 className="text-2xl font-heading font-bold text-slate-900 mb-6">
                Completa tu Pago
              </h2>

              <div className="space-y-6">
                {/* Property Selection */}
                <div>
                  <Label htmlFor="property">Selecciona tu Propiedad</Label>
                  <select
                    id="property"
                    className="w-full mt-2 h-12 rounded-lg border-slate-200 focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200 bg-white px-3"
                    value={selectedProperty || ''}
                    onChange={(e) => setSelectedProperty(e.target.value)}
                    data-testid="property-select"
                  >
                    <option value="">Selecciona una propiedad</option>
                    {properties
                      .filter(prop => {
                        // Filter based on selected plan
                        if (!selectedPlan) return true;
                        if (selectedPlan.type === 'publish') {
                          return prop.status === 'DRAFT' || prop.status === 'PENDING';
                        } else if (selectedPlan.type.startsWith('feature')) {
                          return prop.status === 'APPROVED' && !prop.is_featured;
                        }
                        return true;
                      })
                      .map((prop) => (
                        <option key={prop.id} value={prop.id}>
                          {prop.title} - {prop.status === 'DRAFT' ? 'Borrador' : prop.status === 'PENDING' ? 'Pendiente' : 'Aprobado'}
                        </option>
                      ))}
                  </select>
                  {selectedPlan && properties.filter(prop => {
                    if (selectedPlan.type === 'publish') return prop.status === 'DRAFT' || prop.status === 'PENDING';
                    if (selectedPlan.type.startsWith('feature')) return prop.status === 'APPROVED' && !prop.is_featured;
                    return true;
                  }).length === 0 && (
                    <p className="text-sm text-amber-600 mt-2">
                      {selectedPlan.type === 'publish' 
                        ? 'No tienes propiedades pendientes de publicar. Crea una nueva propiedad primero.'
                        : 'No tienes propiedades aprobadas disponibles para destacar.'}
                    </p>
                  )}
                </div>

                {/* Buyer Info */}
                <div>
                  <Label htmlFor="name">Nombre Completo</Label>
                  <Input
                    id="name"
                    value={buyerInfo.name}
                    onChange={(e) => setBuyerInfo({ ...buyerInfo, name: e.target.value })}
                    className="mt-2"
                    data-testid="buyer-name"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="email">Correo Electrónico</Label>
                    <Input
                      id="email"
                      type="email"
                      value={buyerInfo.email}
                      onChange={(e) => setBuyerInfo({ ...buyerInfo, email: e.target.value })}
                      className="mt-2"
                      data-testid="buyer-email"
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone">Teléfono</Label>
                    <Input
                      id="phone"
                      value={buyerInfo.phone}
                      onChange={(e) => setBuyerInfo({ ...buyerInfo, phone: e.target.value })}
                      placeholder="+57 300 123 4567"
                      className="mt-2"
                      data-testid="buyer-phone"
                    />
                  </div>
                </div>

                {/* Coupon Code */}
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <Label htmlFor="coupon" className="text-blue-900 font-semibold">¿Tienes un cupón de descuento?</Label>
                  <div className="flex space-x-2 mt-2">
                    <Input
                      id="coupon"
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                      placeholder="CODIGO"
                      className="flex-1"
                      disabled={!!couponValidation}
                      data-testid="coupon-input"
                    />
                    {couponValidation ? (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleRemoveCoupon}
                        className="border-red-500 text-red-500 hover:bg-red-50"
                        data-testid="remove-coupon"
                      >
                        Quitar
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        onClick={handleValidateCoupon}
                        disabled={validatingCoupon || !couponCode.trim()}
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                        data-testid="apply-coupon"
                      >
                        {validatingCoupon ? 'Validando...' : 'Aplicar'}
                      </Button>
                    )}
                  </div>
                  {couponValidation && (
                    <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                      <p className="text-sm text-green-800 font-medium">
                        ✓ Cupón aplicado: {couponCode}
                      </p>
                      <p className="text-xs text-green-700 mt-1">
                        Ahorro: {formatPrice(couponValidation.discount_amount)}
                      </p>
                    </div>
                  )}
                </div>

                {/* Summary */}
                <div className="bg-slate-50 rounded-xl p-6 mt-6">
                  <h3 className="font-semibold text-slate-900 mb-4">Resumen del Pago</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Plan:</span>
                      <span className="font-semibold">{selectedPlan.name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Duración:</span>
                      <span className="font-semibold">{selectedPlan.duration_days} días</span>
                    </div>
                    {couponValidation && (
                      <>
                        <div className="flex justify-between">
                          <span className="text-slate-600">Precio original:</span>
                          <span className="line-through text-slate-500">{formatPrice(selectedPlan.price)}</span>
                        </div>
                        <div className="flex justify-between text-green-600">
                          <span>Descuento ({couponCode}):</span>
                          <span>- {formatPrice(couponValidation.discount_amount)}</span>
                        </div>
                      </>
                    )}
                    <div className="flex justify-between border-t border-slate-200 pt-2 mt-2">
                      <span className="text-slate-900 font-semibold">Total:</span>
                      <span className="text-2xl font-heading font-bold text-primary">
                        {formatPrice(couponValidation?.final_amount || selectedPlan.price)}
                      </span>
                    </div>
                  </div>
                </div>

                <Button
                  onClick={handlePayment}
                  disabled={processing || !selectedProperty}
                  className="w-full bg-primary hover:bg-primary-hover h-12 text-base font-semibold"
                  data-testid="proceed-payment"
                >
                  {processing ? 'Procesando...' : 'Proceder al Pago'}
                </Button>

                <p className="text-xs text-slate-500 text-center">
                  Serás redirigido a MercadoPago para completar el pago de forma segura
                </p>
              </div>
            </motion.div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default PaymentPage;